import React, { useMemo, useCallback, useState, useRef } from 'react';
import { TouchableOpacity, View, StyleSheet, PanResponder, Animated } from 'react-native';
import { Text } from './ui/AppText';
import FormattedText from './FormattedText';

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

const AlternativeOption = ({
  alternative,
  index,
  isSelected,
  showResult,
  onPress,
  onDeselect,
  disabled,
  multipleChoice = false,
  letter,
}) => {
  // Estado para alternativa riscada
  const [isCrossed, setIsCrossed] = useState(false);
  const shakeAnimation = useRef(new Animated.Value(0)).current;

  // Função para toggle crossed com animação
  const toggleCrossed = useCallback(() => {
    if (showResult) return;
    // Alternativa selecionada é desmarcada ao ser riscada.
    // Sem onDeselect (não há como limpar a seleção no pai) mantemos a
    // regra antiga: não risca uma selecionada, para não coexistir os estados.
    if (isSelected) {
      if (!onDeselect) return;
      onDeselect(alternative.id);
    }

    // Animação de shake
    Animated.sequence([
      Animated.timing(shakeAnimation, { toValue: 8, duration: 50, useNativeDriver: true }),
      Animated.timing(shakeAnimation, { toValue: -8, duration: 50, useNativeDriver: true }),
      Animated.timing(shakeAnimation, { toValue: 6, duration: 50, useNativeDriver: true }),
      Animated.timing(shakeAnimation, { toValue: -6, duration: 50, useNativeDriver: true }),
      Animated.timing(shakeAnimation, { toValue: 0, duration: 50, useNativeDriver: true }),
    ]).start();

    setIsCrossed(prev => !prev);
  }, [showResult, isSelected, onDeselect, alternative.id, shakeAnimation]);

  // PanResponder para detectar swipe
  const panResponder = useMemo(() => PanResponder.create({
    onStartShouldSetPanResponder: () => false,
    onMoveShouldSetPanResponder: (evt, gestureState) => {
      return Math.abs(gestureState.dx) > 20 && Math.abs(gestureState.dy) < 20;
    },
    onPanResponderRelease: (evt, gestureState) => {
      if (Math.abs(gestureState.dx) > 40) {
        toggleCrossed();
      }
    },
  }), [toggleCrossed]);

  // Resetar crossed quando a questão muda — DURANTE o render, não em effect.
  // Um effect roda após a pintura, então a questão seguinte apareceria por um
  // instante com o riscado antigo (flash) antes de limpar. Ajustar o estado no
  // render corrige antes de pintar. NÃO usar alternative.id como sinal: os ids
  // são 'a'/'b'/... e se repetem em toda questão, então a instância é
  // reaproveitada (key={alt.id}) e o riscado vazaria. O texto muda a cada
  // questão, servindo de detector de troca.
  const prevTextRef = useRef(alternative.text);
  if (prevTextRef.current !== alternative.text) {
    prevTextRef.current = alternative.text;
    if (isCrossed) setIsCrossed(false);
  }

  // Memoiza os cálculos de cores para evitar recálculos em cada render
  const colors = useMemo(() => {
    const isCorrect = alternative.isCorrect;

    let backgroundColor, borderColor, letterBgColor, letterTextColor, textColor;

    if (!showResult) {
      if (isCrossed) {
        backgroundColor = 'rgba(128, 128, 128, 0.1)';
        borderColor = '#9CA3AF';
        letterBgColor = '#9CA3AF';
        letterTextColor = '#FFFFFF';
        textColor = '#9CA3AF';
      } else {
        backgroundColor = isSelected ? '#3B82F6' : '#F3F4F6';
        borderColor = isSelected ? '#3B82F6' : '#E5E7EB';
        letterBgColor = isSelected ? '#FFFFFF' : '#6B7280';
        letterTextColor = isSelected ? '#3B82F6' : '#FFFFFF';
        textColor = isSelected ? '#FFFFFF' : '#1F2937';
      }
    } else {
      if (isCorrect) {
        backgroundColor = '#10B98130';
        borderColor = '#10B981';
        letterBgColor = '#10B981';
        letterTextColor = '#FFFFFF';
        textColor = '#059669';
      } else if (isSelected && !isCorrect) {
        backgroundColor = '#EF444430';
        borderColor = '#EF4444';
        letterBgColor = '#EF4444';
        letterTextColor = '#FFFFFF';
        textColor = '#DC2626';
      } else {
        backgroundColor = '#F3F4F6';
        borderColor = '#E5E7EB';
        letterBgColor = '#9CA3AF';
        letterTextColor = '#FFFFFF';
        textColor = '#6B7280';
      }
    }

    return { backgroundColor, borderColor, letterBgColor, letterTextColor, textColor };
  }, [showResult, isSelected, alternative.isCorrect, isCrossed]);

  // Memoiza o handler para evitar recriação
  const handlePress = useCallback(() => {
    // Não permitir selecionar alternativa riscada
    if (isCrossed) return;
    if (onPress) {
      onPress(alternative.id);
    }
  }, [onPress, alternative.id, isCrossed]);

  // Memoiza estilos dinâmicos
  const containerStyle = useMemo(() => [
    styles.container,
    { backgroundColor: colors.backgroundColor, borderColor: colors.borderColor },
    isCrossed && !showResult && { opacity: 0.4 },
  ], [colors.backgroundColor, colors.borderColor, isCrossed, showResult]);

  const letterContainerStyle = useMemo(() => [
    styles.letterContainer,
    { backgroundColor: colors.letterBgColor },
  ], [colors.letterBgColor]);

  const letterStyle = useMemo(() => [
    styles.letter,
    { color: colors.letterTextColor },
  ], [colors.letterTextColor]);

  const textStyle = useMemo(() => [
    styles.text,
    { color: colors.textColor },
    isCrossed && !showResult && styles.textCrossed,
  ], [colors.textColor, isCrossed, showResult]);

  const checkboxStyle = useMemo(() => [
    styles.checkbox,
    {
      backgroundColor: isSelected ? '#3B82F6' : 'transparent',
      borderColor: isSelected ? '#3B82F6' : '#D1D5DB',
    },
  ], [isSelected]);

  return (
    <Animated.View
      style={{ transform: [{ translateX: shakeAnimation }] }}
      {...panResponder.panHandlers}
    >
      <TouchableOpacity
        style={containerStyle}
        onPress={handlePress}
        disabled={disabled}
        activeOpacity={0.7}
      >
        <View style={letterContainerStyle}>
          <Text style={letterStyle}>
            {letter || LETTERS[index]}
          </Text>
        </View>

        <FormattedText style={textStyle}>
          {alternative.text}
        </FormattedText>

        {multipleChoice && (
          <View style={checkboxStyle}>
            {isSelected && <Text style={styles.checkmark}>✓</Text>}
          </View>
        )}

        {showResult && alternative.isCorrect && (
          <Text style={styles.correctIcon}>✓</Text>
        )}

        {showResult && isSelected && !alternative.isCorrect && (
          <Text style={styles.wrongIcon}>✗</Text>
        )}
      </TouchableOpacity>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 10,
    borderWidth: 2,
    marginVertical: 4,
  },
  letterContainer: {
    width: 28,
    height: 28,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 10,
  },
  letter: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  text: {
    flex: 1,
    fontSize: 13,
    lineHeight: 18,
  },
  textCrossed: {
    textDecorationLine: 'line-through',
    textDecorationStyle: 'solid',
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 6,
    borderWidth: 2,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 8,
  },
  checkmark: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: 'bold',
  },
  correctIcon: {
    color: '#10B981',
    fontSize: 20,
    fontWeight: 'bold',
    marginLeft: 8,
  },
  wrongIcon: {
    color: '#EF4444',
    fontSize: 20,
    fontWeight: 'bold',
    marginLeft: 8,
  },
});

// Memoiza o componente - só re-renderiza se props mudarem
export default React.memo(AlternativeOption);
